import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid2 as Grid,
  Typography,
  MenuItem,
  TextField,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import PageHeader from '../components/common/PageHeader';
import PartnerSelector from '../components/common/PartnerSelector';
import StatCard from '../components/common/StatCard';
import LoadingScreen from '../components/common/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import { partnerService } from '../services/partnerService';
import { formatNumber, formatMs, getErrorMessage } from '../utils/format';

export default function Analytics() {
  const { user, isAdmin } = useAuth();
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [days, setDays] = useState(30);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        if (isAdmin) {
          const res = await partnerService.list({ limit: 100 });
          const list = res.partners || [];
          setPartners(list);
          if (list[0]) setPartnerId(list[0].id);
        } else if (user?.partnerId) {
          setPartnerId(user.partnerId);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [isAdmin, user?.partnerId]);

  useEffect(() => {
    if (!partnerId) return;
    partnerService
      .getUsage(partnerId, { days, limit: 50 })
      .then(setUsage)
      .catch(err => setError(getErrorMessage(err)));
  }, [partnerId, days]);

  if (loading) return <LoadingScreen />;

  const stats = usage?.stats;
  const totals = stats?.totals || {};
  const byDay = stats?.byDay || [];
  const byDevice = stats?.byDevice || [];
  const today = usage?.today || {};

  const lineData = byDay.map(d => d.count);
  const lineLabels = byDay.map(d => d._id?.slice(5) || '');

  const pieData = byDevice.map((d, i) => ({
    id: i,
    value: d.count,
    label: d._id?.replace(/_/g, ' ') || 'Unknown',
  }));

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle="API usage trends and device breakdown"
        action={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {isAdmin && (
              <PartnerSelector partners={partners} value={partnerId} onChange={setPartnerId} />
            )}
            <TextField
              select
              size="small"
              label="Period"
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              sx={{ minWidth: 120 }}
            >
              {[7, 14, 30, 90].map(d => (
                <MenuItem key={d} value={d}>
                  {d} days
                </MenuItem>
              ))}
            </TextField>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Total Requests" value={formatNumber(totals.total)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Errors" value={formatNumber(totals.errors)} color="error.main" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Avg Response" value={formatMs(totals.avgResponseMs)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Today Used"
            value={`${formatNumber(today.used)} / ${formatNumber(today.limit)}`}
            subtitle={`${formatNumber(today.remaining)} remaining`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily API Volume
              </Typography>
              {lineData.length ? (
                <LineChart
                  height={320}
                  series={[{ data: lineData, label: 'Requests', color: '#00838f' }]}
                  xAxis={[{ scaleType: 'point', data: lineLabels }]}
                  margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
                />
              ) : (
                <Typography color="text.secondary" align="center" py={8}>
                  No data for selected period
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                By Device Type
              </Typography>
              {pieData.length ? (
                <PieChart
                  series={[{ data: pieData, innerRadius: 40, outerRadius: 100 }]}
                  height={320}
                  margin={{ top: 10, bottom: 10 }}
                />
              ) : (
                <Typography color="text.secondary" align="center" py={8}>
                  No device breakdown
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
